import { css } from "@emotion/react"
import { TrustpilotStar } from "../visual/TrustpilotStars"
import { responsiveHeadingLevel } from "../../helpers/css"
import { xlScaleFactor } from "../../constants/sizes"
import { useRedoitTheme } from "../../theme"
import { ComponentPropsWithoutRef } from "react"

type ElementTypeMap = {
    a: HTMLAnchorElement
    button: HTMLButtonElement
    div: HTMLDivElement
}

type Element = keyof ElementTypeMap

type TrustpilotButtonProps<E extends Element = "div"> = {
    /**
     * Render as a different element than div.
     */
    as?: E

    /**
     * The score to show on the button.
     */
    score: number

    /**
     * The text to show on the button
     */
    text?: string
} & Omit<ComponentPropsWithoutRef<E>, "ref">

export function TrustpilotButton<E extends Element = "div">({
    as,
    score,
    text,
    ...rest
}: TrustpilotButtonProps<E>) {
    const { colors } = useRedoitTheme()
    const style = {
        default: {
            color: colors.gray500,
            borderColor: colors.gray200,
        },
        hover: { borderColor: colors.gray400 },
        focus: { borderColor: colors.gray500 },
    }
    const El = as ?? "div"

    return (
        <El
            css={css(
                {
                    display: "flex",
                    alignItems: "center",
                    borderStyle: "solid",
                    borderWidth: 1,
                    height: 48 * xlScaleFactor,
                    borderRadius: 24,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 1,
                    backgroundColor: colors.grayWhite,
                },
                responsiveHeadingLevel("4"),
                style.default
            )}
            {...(rest as any)}
        >
            {score.toString().replace(".", ",")}
            <TrustpilotStar css={css({ marginLeft: 6, marginRight: 6 })} />
            {text}
        </El>
    )
}
